/**
 * "Try one case": one real agent call on one dataset case, with exactly the
 * connection, version, mapping and run settings picked, then the answer scored
 * by the chosen evaluators as the run would score it. Shows the output (as a
 * JSON tree when it is JSON, as text otherwise) or the error as is, the
 * version that answered, how long it took, and the scores below.
 * Nothing is saved; it counts as a normal agent call (and judge calls cost). The picked
 * case's input shows on the left; the agent's answer fills the right.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { JsonView } from "@/components/ui/json-view";
import { errorText } from "@/components/ui/states";
import { useDatasetItems } from "@/features/datasets/hooks";
import { formatLatency } from "@/lib/format";
import { Select } from "@/components/ui/select";
import { itemLabel } from "@/lib/item-label";
import { useTryCase } from "../hooks";
import { tryRequest } from "./new-experiment-payload";
import type { TryCaseResult } from "../types";
import type { NewExperiment } from "./use-new-experiment";
import { CaseColumns, itemInput } from "./case-preview";
import { CaseScores } from "./case-scores";

function Result({ result }: { result: TryCaseResult }) {
  const drift = result.reported_version && result.version && result.reported_version !== result.version;
  return (
    <section aria-label="Try result" className="flex flex-col gap-2">
      <p className="flex flex-wrap gap-x-3 text-xs text-muted">
        <span>
          Called{" "}
          <span className="font-mono text-body">{result.version ? `v${result.version}` : "the agent"}</span>
        </span>
        <span>
          Answered by{" "}
          <span className={`font-mono ${drift ? "text-warn" : "text-body"}`}>
            {result.reported_version ? `v${result.reported_version}` : "unknown version"}
          </span>
        </span>
        <span>
          Took <span className="font-mono text-body">{formatLatency(result.latency_ms)}</span>
        </span>
      </p>
      {result.error ? (
        <div role="alert" className="rounded-lg bg-err-bg px-3.5 py-2.5 text-[13px] text-err">
          {result.error_code && <strong className="font-mono">{result.error_code}: </strong>}
          {result.error}
        </div>
      ) : (
        <JsonView value={result.output} label="Output" className="max-h-[40vh]" />
      )}
    </section>
  );
}

export function TryOneCase({ flow }: { flow: NewExperiment }) {
  const items = useDatasetItems(flow.datasetId ?? "", flow.datasetId ? flow.version : null, 1);
  const [picked, setPicked] = useState<string | null>(null);
  const tryCase = useTryCase();
  const list = items.data?.items ?? [];
  const itemId = picked ?? list[0]?.id ?? null;
  const item = list.find((i) => i.id === itemId);
  const request = tryRequest(flow, itemId);
  return (
    <fieldset className="flex flex-col gap-3 rounded-[9px] border border-line p-3.5">
      <legend className="px-1 text-[13px] font-semibold">Try one case</legend>
      <p className="-mt-1 text-xs text-muted">
        Runs the agent once on one case and scores the answer, as the experiment would. Nothing is saved.
      </p>
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Field label="Case" htmlFor="try-case">
            <Select
              id="try-case"
              value={itemId ?? ""}
              onChange={(v) => {
                setPicked(v);
                tryCase.reset();
              }}
              options={list.map((item) => ({ value: item.id, label: itemLabel(item.position, item.input) }))}
            />
          </Field>
        </div>
        <Button disabled={!request || tryCase.isPending} onClick={() => request && tryCase.mutate(request)}>
          {tryCase.isPending ? "Running…" : "Try one case"}
        </Button>
      </div>
      {tryCase.isError && (
        <p role="alert" className="text-[13px] text-err">
          {errorText(tryCase.error, "Could not run the case.")}
        </p>
      )}
      {item && (
        <CaseColumns
          input={itemInput(item)}
          outputTitle="Agent output"
          output={
            tryCase.data ? (
              <Result result={tryCase.data} />
            ) : (
              <p className="rounded-lg border border-dashed border-line px-3.5 py-6 text-center text-xs text-muted">
                {tryCase.isPending ? "Running…" : "Try the case to see the agent's answer."}
              </p>
            )
          }
        />
      )}
      {tryCase.data && tryCase.data.scores.length > 0 && (
        <CaseScores scores={tryCase.data.scores} label="Try scores" />
      )}
    </fieldset>
  );
}
